import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
  RefreshCw,
  Inbox,
} from "lucide-react";
import dayjs from "dayjs";
import { getClientLedger } from "../services/authService";
import { getClientData, toIndianRupeeFormat } from "./custom";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "deposit", label: "Deposit" },
  { key: "withdraw", label: "Withdraw" },
];

function LedgerMaster() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("INR"); // INR | Forex
  const [activeFilter, setActiveFilter] = useState("all"); // all | deposit | withdraw
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleGoBack = () => navigate("/account");

  const isInr = (item) =>
    (item.currency_code || "INR").toUpperCase() === "INR" ||
    item.currency_symbol === "₹";

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getClientLedger();
      setRecords(response?.data?.records || []);
      setSummary(response?.data?.summary || null);
    } catch (err) {
      setError("Unable to load ledger. Please try again.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Records for the active currency tab.
  const tabRecords = useMemo(
    () =>
      records.filter((item) =>
        activeTab === "INR" ? isInr(item) : !isInr(item)
      ),
    [records, activeTab]
  );

  // Records after applying the deposit / withdraw chip.
  const visibleRecords = useMemo(
    () =>
      activeFilter === "all"
        ? tabRecords
        : tabRecords.filter(
            (item) => (item.type || "").toLowerCase() === activeFilter
          ),
    [tabRecords, activeFilter]
  );

  // Tab-level totals (independent of the chip so the pills stay stable).
  const totals = useMemo(() => {
    let deposit = 0;
    let withdraw = 0;
    tabRecords.forEach((item) => {
      if ((item.status || "").toLowerCase() !== "completed") return;
      if ((item.type || "").toLowerCase() === "deposit") {
        deposit += Number(item.amount) || 0;
      } else {
        withdraw += Number(item.amount) || 0;
      }
    });
    return { deposit, withdraw, net: deposit - withdraw };
  }, [tabRecords]);

  const availableBalance = useMemo(() => {
    if (summary) {
      return activeTab === "INR"
        ? summary.available_balance
        : summary.available_balance_usd;
    }
    return getClientData()?.client_account?.balance ?? 0;
  }, [summary, activeTab]);

  const symbol = activeTab === "INR" ? "₹" : "$";

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "completed") return null;
    const map = {
      pending: "text-amber-700 bg-amber-100",
      cancel: "text-red-700 bg-red-100",
      failed: "text-red-700 bg-red-100",
    };
    return (
      <span
        className={`font-lato text-[10px] font-semibold uppercase px-2 py-[2px] rounded-full ${
          map[s] || "text-gray-600 bg-gray-100"
        }`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div
        className="h-screen overflow-y-scroll p-4 pb-24 flex-grow max-w-sm min-w-[375px] w-full bg-[#f6f7fb]
            [&::-webkit-scrollbar]:w-1
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-transparent
            scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1">
            <ChevronLeft
              size={18}
              onClick={handleGoBack}
              className="cursor-pointer"
            />
            <span className="font-lato font-semibold text-sm">Ledger Master</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchLedger}
              aria-label="Refresh"
              className="p-[6px] rounded-full bg-white shadow-sm active:scale-95 transition"
            >
              <RefreshCw
                size={14}
                className={`text-[#536895] ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <div role="tablist" className="flex bg-[#536895] bg-opacity-25 rounded-[10px]">
              {["INR", "Forex"].map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`${
                    activeTab === tab
                      ? "bg-[#536895] text-white"
                      : "bg-transparent text-black"
                  } font-lato text-xs leading-[18px] rounded-[10px] py-[8px] px-[16px] text-center transition-colors`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Balance hero card */}
        <div className="rounded-2xl p-4 bg-gradient-to-br from-[#536895] to-[#3c4c72] text-white shadow-md">
          <div className="flex items-center justify-between">
            <span className="font-lato text-xs text-white/70">
              Available Balance
            </span>
            <div className="bg-white/15 p-2 rounded-full">
              <Wallet size={16} className="text-white" />
            </div>
          </div>
          <div className="font-lato text-2xl font-bold mt-1 tracking-wide">
            {symbol} {toIndianRupeeFormat(availableBalance)}
          </div>
          <div className="font-lato text-[11px] text-white/70 mt-1">
            Net movement:{" "}
            <span
              className={
                totals.net >= 0 ? "text-emerald-200" : "text-red-200"
              }
            >
              {totals.net >= 0 ? "+" : "-"}
              {symbol} {toIndianRupeeFormat(Math.abs(totals.net))}
            </span>
          </div>
        </div>

        {/* Deposit / Withdraw stat pills */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-100 p-[6px] rounded-full">
                <ArrowDownLeft size={14} className="text-emerald-700" />
              </div>
              <span className="font-lato text-[11px] text-gray-500">
                Total Deposit
              </span>
            </div>
            <div className="font-lato text-base font-bold text-emerald-700 mt-1">
              {symbol} {toIndianRupeeFormat(totals.deposit)}
            </div>
          </div>

          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="bg-red-100 p-[6px] rounded-full">
                <ArrowUpRight size={14} className="text-red-700" />
              </div>
              <span className="font-lato text-[11px] text-gray-500">
                Total Withdraw
              </span>
            </div>
            <div className="font-lato text-base font-bold text-red-700 mt-1">
              {symbol} {toIndianRupeeFormat(totals.withdraw)}
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 mt-4 mb-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setActiveFilter(f.key)}
              className={`${
                activeFilter === f.key
                  ? "bg-[#536895] text-white"
                  : "bg-white text-gray-600"
              } font-lato text-xs font-semibold py-[6px] px-3 rounded-full shadow-sm transition-colors`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Ledger list */}
        {loading ? (
          <LedgerSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="font-lato text-sm text-gray-500 mb-3">{error}</p>
            <button
              type="button"
              onClick={fetchLedger}
              className="font-lato text-xs font-semibold text-white bg-[#536895] px-4 py-2 rounded-full"
            >
              Retry
            </button>
          </div>
        ) : visibleRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="bg-[#536895]/10 p-4 rounded-full mb-3">
              <Inbox size={26} className="text-[#536895]" />
            </div>
            <p className="font-lato text-sm font-semibold text-gray-700">
              No ledger entries
            </p>
            <p className="font-lato text-xs text-gray-400 mt-1">
              Your {activeTab} deposits &amp; withdrawals will appear here.
            </p>
          </div>
        ) : (
          <LedgerList records={visibleRecords} symbol={symbol} statusBadge={statusBadge} />
        )}
      </div>
    </div>
  );
}

function LedgerList({ records, symbol, statusBadge }) {
  let lastDay = null;

  return (
    <div className="flex flex-col gap-2">
      {records.map((item, index) => {
        const isDeposit = (item.type || "").toLowerCase() === "deposit";
        const day = dayjs(item.created_at).format("DD MMM YYYY");
        const showDivider = day !== lastDay;
        lastDay = day;

        return (
          <React.Fragment key={item.transaction_id || index}>
            {showDivider && (
              <div className="flex items-center gap-2 mt-2 mb-1">
                <span className="font-lato text-[11px] font-semibold text-gray-400">
                  {day}
                </span>
                <div className="flex-grow h-px bg-gray-200" />
              </div>
            )}

            <div className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`p-2 rounded-full shrink-0 ${
                    isDeposit ? "bg-emerald-100" : "bg-red-100"
                  }`}
                >
                  {isDeposit ? (
                    <ArrowDownLeft size={16} className="text-emerald-700" />
                  ) : (
                    <ArrowUpRight size={16} className="text-red-700" />
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-lato text-sm font-semibold text-gray-800">
                      {isDeposit ? "Deposit" : "Withdraw"}
                    </span>
                    {statusBadge(item.status)}
                  </div>
                  <span className="font-lato text-[11px] text-gray-400">
                    {dayjs(item.created_at).format("hh:mm A")}
                    {item.method ? ` · ${item.method}` : ""}
                  </span>
                  {item.note && (
                    <p className="font-lato text-[11px] text-gray-400 truncate max-w-[150px]">
                      {item.note}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0">
                <div
                  className={`font-lato text-sm font-bold ${
                    isDeposit ? "text-emerald-700" : "text-red-700"
                  }`}
                >
                  {isDeposit ? "+" : "-"}
                  {symbol} {toIndianRupeeFormat(item.amount)}
                </div>
                <span className="font-lato text-[11px] text-gray-400">
                  Bal: {symbol} {toIndianRupeeFormat(item.running_balance)}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function LedgerSkeleton() {
  return (
    <div className="flex flex-col gap-2 animate-pulse">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-xl p-3 shadow-sm flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200" />
            <div className="flex flex-col gap-2">
              <div className="w-20 h-3 rounded bg-gray-200" />
              <div className="w-14 h-2 rounded bg-gray-100" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="w-16 h-3 rounded bg-gray-200" />
            <div className="w-12 h-2 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default LedgerMaster;
